import * as Sentry from '@sentry/react-native';

const REPORTED_ERROR_SYMBOL = Symbol('reportedToSentry');

export type ReportErrorRequestContext = {
  durationMs?: number;
  method?: string;
  retry?: boolean;
  status?: number;
  url?: string;
};

export type ReportErrorOptions = {
  action?: string;
  capture?: boolean;
  extra?: Record<string, unknown>;
  fallbackMessage?: string;
  feature?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  request?: ReportErrorRequestContext;
  screen?: string;
  tags?: Record<string, string>;
};

const markReported = (candidate: unknown) => {
  if (!candidate || typeof candidate !== 'object') {
    return;
  }

  try {
    Object.defineProperty(candidate, REPORTED_ERROR_SYMBOL, {
      configurable: true,
      enumerable: false,
      value: true,
      writable: false,
    });
  } catch {
    // Best-effort only.
  }
};

export const isReportedError = (candidate: unknown): boolean =>
  Boolean(candidate && typeof candidate === 'object' && (candidate as any)[REPORTED_ERROR_SYMBOL]);

export const normalizeError = (
  error: unknown,
  fallbackMessage = 'Something went wrong',
): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return new Error(error);
  }

  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      const normalized = new Error(message);
      (normalized as Error & { cause?: unknown }).cause = error;
      return normalized;
    }

    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(fallbackMessage);
    }
  }

  return new Error(fallbackMessage);
};

export const reportError = (
  error: unknown,
  options: ReportErrorOptions = {},
): Error => {
  const normalizedError = normalizeError(
    error,
    options.fallbackMessage || 'Something went wrong',
  );

  if (options.capture === false) {
    return normalizedError;
  }

  if (isReportedError(error) || isReportedError(normalizedError)) {
    return normalizedError;
  }

  Sentry.withScope(scope => {
    if (options.feature) {
      scope.setTag('feature', options.feature);
    }
    if (options.screen) {
      scope.setTag('screen', options.screen);
    }
    if (options.action) {
      scope.setTag('action', options.action);
    }
    if (options.level) {
      scope.setLevel(options.level);
    }
    if (options.request) {
      scope.setContext('request', options.request);
    }
    if (options.extra) {
      scope.setExtras(options.extra);
    }
    if (options.tags) {
      scope.setTags(options.tags);
    }

    Sentry.captureException(normalizedError);
  });

  markReported(normalizedError);
  markReported(error);

  return normalizedError;
};

